<template>
  <section id="accounts-index-page">
    <page-header
      :title="$dictionary.app.accounts.index.title"
      :breadcrumbs="breadcrumbs"
    >
      <v-col cols="12" md="8" lg="6" class="d-flex align-center justify-end">
        <debounce-search-text-field
          prop-to-search="name"
          :lazy-search="getAccountsList"
          placeholder="Search Accounts..."
          :sort-by="sortConfig.sortBy"
          :no-of-records="accountsParams.per_page"
          :sort-order="sortConfig.sortOrder"
          @search-value="searchAccount = $event"
        />
        <div id="accounts-csv-btn">
          <csv-button
            :labels="labels"
            :data="accounts"
            file-name="accounts"
            :fields="csvFields"
            :loading="isLoading"
            :color="$appConfig.csvButton.color.secondary"
          />
        </div>
      </v-col>
    </page-header>
    <v-row class="d-flex justify-center pt-3">
      <v-col cols="12" lg="12" md="12">
        <accounts-table
          :data="accounts"
          :is-loading="isLoading"
          @get-records="getAccountsList"
          default-sort-order="name"
        />
      </v-col>
    </v-row>
  </section>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import CsvButton from "@/components/widgets/CsvButton.vue";
import PageHeader from "@/components/shared/PageHeader.vue";

import { csvFileLabels, hasProp } from "@/utils/common.utils";
import AccountsTable from "@/components/tables/AccountsTable.vue";
import headers from "@/data/table-headers/accounts-index.headers.json";

import TitleMixin from "@/mixins/Title.mixin";
import SharedUtilsMixin from "@/mixins/SharedUtils.mixin";
import DebounceSearchTextField from "@/components/shared/DebounceSearchTextField.vue";

import { SORTING_ORDERS } from "@/constants";

/**
 * Accounts index page
 */
export default {
  name: "Accounts",
  /**
   * Title of the page
   */
  title: "Accounts",
  /**
  |--------------------------------------------------
  | Mixins
  |--------------------------------------------------
  */
  mixins: [TitleMixin, SharedUtilsMixin],
  /**
  |--------------------------------------------------
  | Components
  |--------------------------------------------------
  */
  components: {
    CsvButton,
    PageHeader,
    AccountsTable,
    DebounceSearchTextField,
  },
  /**
  |--------------------------------------------------
  | Data properties
  |--------------------------------------------------
  */
  data() {
    return {
      isLoading: false,
      accountsParams: { sortBy: "name" },
      searchAccount: "",
      labels: csvFileLabels(headers),
    };
  },
  /**
  |--------------------------------------------------
  | Computed properties
  |--------------------------------------------------
  */
  computed: {
    /**
     * Maps store getters in component
     */
    ...mapGetters({
      accounts: "account/accounts",
    }),
    /**
     * Csv fields to be downloade
     */
    csvFields() {
      return Object.keys(this.labels);
    },
    /**
     * View breadcrumb array
     */
    breadcrumbs() {
      return [
        {
          title: this.$dictionary.app.accounts.index.title,
          type: this.$appConfig.breadcrumbs.types.route,
        },
      ];
    },
    /**
     * Sorting config of accounts view table
     */
    sortConfig() {
      return this.getSortConfig("accountsParams", SORTING_ORDERS.asc);
    },
  },
  /**
  |--------------------------------------------------
  | Methods
  |--------------------------------------------------
  */
  methods: {
    /**
     * Map store actions in component
     */
    ...mapActions({
      getAccounts: "account/getAccounts",
    }),
    ...mapMutations({
      setAccounts: "account/SET_ACCOUNTS",
    }),
    /**
     * Adds search property in params object
     */
    addSearchProp() {
      if (!hasProp(this.accountsParams, "q[name_cont]") && this.searchAccount) {
        this.accountsParams["q[name_cont]"] = this.searchAccount;
      }
    },
    /**
     * Fetch accounts
     * @param {Object} params Accounts get aou request params
     */
    async getAccountsList(params = this.accountsParams) {
      try {
        this.isLoading = true;
        this.accountsParams = params;
        this.addSearchProp();
        await this.getAccounts(params);
      } finally {
        this.isLoading = false;
      }
    },
  },
  /**
   * Sets accounts as empty array before route is left
   */
  beforeDestroy() {
    this.setAccounts([]);
  },
};
</script>
